import React, { useEffect, useState } from 'react'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import { useApi } from '../../hooks/useApi'
import { formatDuration } from '../../hooks/useCrons'

interface Status {
  suspend: boolean
  schedule: string
  name: string
  lastCompletionTime: any
  lastStartTime: any
  lastScheduleTime: any
  nextScheduleTime: any
  _id: string
  active: boolean
}

const Crons: React.FC = () => {
  const { callApi } = useApi()
  const [data, setData] = useState<Status[] | null>(null)

  useEffect(() => {
    callApi('/admin/crons')
      .then(setData)
      .catch((e) => console.error('Error loading crons:', e))
  }, [])

  const crons = data?.filter((o) => !o.name.startsWith('relayer-executor-cron')) ?? []
  const relayers = data?.filter((o) => o.name.startsWith('relayer-executor-cron')) ?? []

  if (!data) return <LoaderBar />

  return (
    <Wrap>
      <SectionTitle>Relayer Executor</SectionTitle>
      <CronTable items={relayers} />

      <SectionTitle>Others</SectionTitle>
      <CronTable items={crons} />
    </Wrap>
  )
}

function CronTable({ items }: { items: Status[] }) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Duration</Th>
            <Th>Last start</Th>
            <Th>Last completion</Th>
            <Th>Next schedule</Th>
            <Th>Schedule</Th>
            <Th>Suspended</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const duration = moment.duration(moment(item.lastCompletionTime).diff(moment(item.lastStartTime)))
            const isStale = duration.asMinutes() < 2 && item.active !== true

            return (
              <tr key={index}>
                <Td><CronName>{item.name}</CronName></Td>
                <Td>
                  <Mono>{formatDuration(duration)}</Mono>
                  {isStale && <WarnBadge>stale</WarnBadge>}
                </Td>
                <Td><Mono>{moment(item.lastStartTime).format('DD/MM/YY HH:mm')}</Mono></Td>
                <Td>
                  {item.active === true
                    ? <RunningBadge>Running</RunningBadge>
                    : <Mono>{moment(item.lastCompletionTime).format('DD/MM/YY HH:mm')}</Mono>}
                </Td>
                <Td><Mono>{moment(item.nextScheduleTime).format('DD/MM/YY HH:mm')}</Mono></Td>
                <Td><Mono>{item.schedule}</Mono></Td>
                <Td>
                  <StatusDot ok={!item.suspend} title={item.suspend ? 'Suspended' : 'Active'} />
                </Td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </TableWrap>
  )
}

export default Crons

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SectionTitle = styled.h2`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #777;
  margin: 0;
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111, 92, 230, 0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  tr:last-child & { border-bottom: none; }
`

const CronName = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #ededed;
`

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #a0a0a0;
`

const StatusDot = styled.span<{ ok: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ ok }) => ok ? '#33C2B0' : '#ff4444'};
`

const WarnBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  background: rgba(255, 204, 51, 0.1);
  color: #FFCC33;
  border: 1px solid rgba(255, 204, 51, 0.25);
  margin-left: 6px;
`

const RunningBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  background: rgba(111, 92, 230, 0.1);
  color: #a89aff;
  border: 1px solid rgba(111, 92, 230, 0.25);
`
